/**
 * Farm Map Redux Slice
 *
 * Manages state for the farm map page: overview data, field marks, selection, and UI state.
 */
import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import farmMapService from '@/services/farmMapService';
import { isInFlight, markInFlight, clearInFlight } from '@/store/inFlight';
import fieldMarkService from '@/services/fieldMarkService';
import type {
  FarmMapOverview,
  SectorIrrigationStatus,
  SectorCwsiStatus,
} from '@/services/farmMapService';
import type { FieldMark } from '@/services/fieldMarkService';

type SelectionType = 'sector' | 'device' | 'fieldMark' | 'field';

export type MapVisualizationMode = 'stress' | 'irrigation' | 'cwsi';

interface GpsCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface IrrigationStatusState {
  bySector: Record<number, SectorIrrigationStatus>;
  generatedAt: string | null;
  loading: boolean;
  error: string | null;
}

interface CwsiStatusState {
  bySector: Record<number, SectorCwsiStatus>;
  generatedAt: string | null;
  loading: boolean;
  error: string | null;
}

interface FarmMapState {
  overview: FarmMapOverview | null;
  fieldMarks: FieldMark[];
  selectedType: SelectionType | null;
  selectedId: number | string | null;
  toolbarOpen: boolean;
  bottomSheetState: 'closed' | 'peek' | 'half' | 'full';
  fieldMarkStatusFilter: string;
  gpsPosition: GpsCoords | null;
  loading: boolean;
  error: string | null;
  visualizationMode: MapVisualizationMode;
  irrigationStatus: IrrigationStatusState;
  cwsiStatus: CwsiStatusState;
  /** Device types hidden on the map (session-only, not persisted). */
  hiddenDeviceTypes: string[];
  /** Field-mark visibility on the map by status group (session-only). Active =
   *  pending/in_progress (shown by default); resolved = resolved/cancelled
   *  (hidden by default). Archived marks are never shown. */
  fieldMarkVisibility: { active: boolean; resolved: boolean };
}

const emptyStatusState = { bySector: {}, generatedAt: null, loading: false, error: null };

const initialState: FarmMapState = {
  overview: null,
  fieldMarks: [],
  selectedType: null,
  selectedId: null,
  toolbarOpen: true,
  bottomSheetState: 'closed',
  fieldMarkStatusFilter: '',
  gpsPosition: null,
  loading: false,
  error: null,
  // Irrigation is the default: "what is watering right now" is what operators
  // open the map for, asked for repeatedly. Stress is the second mode.
  visualizationMode: 'irrigation',
  irrigationStatus: { ...emptyStatusState },
  cwsiStatus: { ...emptyStatusState },
  hiddenDeviceTypes: [],
  fieldMarkVisibility: { active: true, resolved: false },
};

export const fetchFarmMapOverview = createAsyncThunk(
  'farmMap/fetchOverview',
  async (farmId: string) => {
    const key = `mapOverview:${farmId}`;
    markInFlight(key);
    try {
      return await farmMapService.getFarmMapOverview(farmId);
    } finally {
      clearInFlight(key);
    }
  },
  { condition: (farmId: string) => !isInFlight(`mapOverview:${farmId}`) }
);

export const fetchFieldMarks = createAsyncThunk(
  'farmMap/fetchFieldMarks',
  async (farmId: string) => {
    return await fieldMarkService.list(farmId);
  }
);

export const fetchIrrigationStatus = createAsyncThunk(
  'farmMap/fetchIrrigationStatus',
  async (farmId: string) => {
    return await farmMapService.getIrrigationStatus(farmId);
  }
);

export const fetchCwsiStatus = createAsyncThunk(
  'farmMap/fetchCwsiStatus',
  async (farmId: string) => {
    return await farmMapService.getCwsiStatus(farmId);
  }
);

const farmMapSlice = createSlice({
  name: 'farmMap',
  initialState,
  reducers: {
    selectItem: (state, action: PayloadAction<{ type: SelectionType; id: number | string }>) => {
      state.selectedType = action.payload.type;
      state.selectedId = action.payload.id;
      state.bottomSheetState = 'half';  // mobile
      state.toolbarOpen = true;          // desktop: always open panel on selection
    },
    clearSelection: (state) => {
      state.selectedType = null;
      state.selectedId = null;
      state.bottomSheetState = 'closed';
    },
    setToolbarOpen: (state, action: PayloadAction<boolean>) => {
      state.toolbarOpen = action.payload;
    },
    setBottomSheetState: (state, action: PayloadAction<'closed' | 'peek' | 'half' | 'full'>) => {
      state.bottomSheetState = action.payload;
    },
    setFieldMarkStatusFilter: (state, action: PayloadAction<string>) => {
      state.fieldMarkStatusFilter = action.payload;
    },
    resetOverview: (state) => {
      state.overview = null;
      state.loading = false;
      state.error = null;
      state.irrigationStatus = { ...emptyStatusState };
      state.cwsiStatus = { ...emptyStatusState };
    },
    setVisualizationMode: (state, action: PayloadAction<MapVisualizationMode>) => {
      state.visualizationMode = action.payload;
    },
    toggleDeviceType: (state, action: PayloadAction<string>) => {
      const type = action.payload;
      if (state.hiddenDeviceTypes.includes(type)) {
        state.hiddenDeviceTypes = state.hiddenDeviceTypes.filter((t) => t !== type);
      } else {
        state.hiddenDeviceTypes.push(type);
      }
    },
    toggleFieldMarkVisibility: (state, action: PayloadAction<'active' | 'resolved'>) => {
      state.fieldMarkVisibility[action.payload] = !state.fieldMarkVisibility[action.payload];
    },
    addFieldMark: (state, action: PayloadAction<FieldMark>) => {
      state.fieldMarks.unshift(action.payload);
    },
    updateFieldMark: (state, action: PayloadAction<FieldMark>) => {
      const idx = state.fieldMarks.findIndex((m) => m.id === action.payload.id);
      if (idx !== -1) state.fieldMarks[idx] = action.payload;
    },
    setGpsPosition: (state, action: PayloadAction<GpsCoords | null>) => {
      state.gpsPosition = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFarmMapOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFarmMapOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(fetchFarmMapOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch map data';
      })
      .addCase(fetchFieldMarks.fulfilled, (state, action) => {
        state.fieldMarks = action.payload;
      })
      // Keep stale data while refreshing: only show loading on first load so
      // WS/poll-triggered refetches don't flicker the map.
      .addCase(fetchIrrigationStatus.pending, (state) => {
        if (state.irrigationStatus.generatedAt == null) {
          state.irrigationStatus.loading = true;
        }
        state.irrigationStatus.error = null;
      })
      .addCase(fetchIrrigationStatus.fulfilled, (state, action) => {
        state.irrigationStatus.loading = false;
        state.irrigationStatus.generatedAt = action.payload.generated_at;
        state.irrigationStatus.bySector = Object.fromEntries(
          action.payload.sectors.map((s) => [s.sector_id, s])
        );
      })
      .addCase(fetchIrrigationStatus.rejected, (state, action) => {
        state.irrigationStatus.loading = false;
        state.irrigationStatus.error = action.error.message || 'Failed to fetch irrigation status';
      })
      .addCase(fetchCwsiStatus.pending, (state) => {
        if (state.cwsiStatus.generatedAt == null) {
          state.cwsiStatus.loading = true;
        }
        state.cwsiStatus.error = null;
      })
      .addCase(fetchCwsiStatus.fulfilled, (state, action) => {
        state.cwsiStatus.loading = false;
        state.cwsiStatus.generatedAt = action.payload.generated_at;
        state.cwsiStatus.bySector = Object.fromEntries(
          action.payload.sectors.map((s) => [s.sector_id, s])
        );
      })
      .addCase(fetchCwsiStatus.rejected, (state, action) => {
        state.cwsiStatus.loading = false;
        state.cwsiStatus.error = action.error.message || 'Failed to fetch CWSI status';
      });
  },
});

export const {
  selectItem,
  clearSelection,
  resetOverview,
  setToolbarOpen,
  setBottomSheetState,
  setFieldMarkStatusFilter,
  addFieldMark,
  updateFieldMark,
  setGpsPosition,
  setVisualizationMode,
  toggleDeviceType,
  toggleFieldMarkVisibility,
} = farmMapSlice.actions;
export const farmMapReducer = farmMapSlice.reducer;

// Selectors
export const selectFarmMapOverview = (state: { farmMap: FarmMapState }) => state.farmMap.overview;
export const selectFarmMapLoading = (state: { farmMap: FarmMapState }) => state.farmMap.loading;
export const selectFarmMapError = (state: { farmMap: FarmMapState }) => state.farmMap.error;
// Memoized: builds a { type, id } object, so it must be cached or it returns a
// new reference every call (unnecessary re-renders / selector stability warning).
export const selectFarmMapSelection = createSelector(
  (state: { farmMap: FarmMapState }) => state.farmMap.selectedType,
  (state: { farmMap: FarmMapState }) => state.farmMap.selectedId,
  (type, id) => ({ type, id }),
);
export const selectToolbarOpen = (state: { farmMap: FarmMapState }) => state.farmMap.toolbarOpen;
export const selectBottomSheetState = (state: { farmMap: FarmMapState }) => state.farmMap.bottomSheetState;
export const selectFieldMarks = (state: { farmMap: FarmMapState }) => state.farmMap.fieldMarks;
export const selectFieldMarkStatusFilter = (state: { farmMap: FarmMapState }) => state.farmMap.fieldMarkStatusFilter;
export const selectGpsPosition = (state: { farmMap: FarmMapState }) => state.farmMap.gpsPosition;
export const selectVisualizationMode = (state: { farmMap: FarmMapState }) => state.farmMap.visualizationMode;
export const selectIrrigationStatus = (state: { farmMap: FarmMapState }) => state.farmMap.irrigationStatus;
export const selectCwsiStatus = (state: { farmMap: FarmMapState }) => state.farmMap.cwsiStatus;
export const selectHiddenDeviceTypes = (state: { farmMap: FarmMapState }) => state.farmMap.hiddenDeviceTypes;
export const selectFieldMarkVisibility = (state: { farmMap: FarmMapState }) => state.farmMap.fieldMarkVisibility;
